// DiscountManagement.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import {
  addNewTrentIcon,
  AddUserIcon,
  AnalyticsIcon,
  BackArrowIcon,
  BlueDownloadExportIcon,
  DownArrowSelect,
  DownloadExportIcon,
  IntelligenceIcon,
  LaptopIcon,
  PaginationLeftArrow,
  PaginationRightArrow,
  ProgramIcon,
  RefreshWhiteIcon,
  WhiteBellIcon,
} from "../../assets/icons";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import TabsHeader from "../../components/TabsHeader";
import { PlusIcon } from "../../assets/icons";
import { Link, useNavigate } from "react-router-dom";
import ProgramsTab from "./ProgramsTab";
import CodesTab from "./codesTab";
import ApplicationTab from "./ApplicationTab";
import AnalyticsTabs from "./AnalyticsTabs";
import PopUpModal from "../../modals/PopUpModal";
import { useDispatch, useSelector } from "react-redux";
import {
  reqToCreateDiscountProgram,
  reqToGetDiscountVariables,
  reqToGetProgramList,
} from "../../redux/slices.js/discountmanagement/discountPromotionSlice";

const tabs = [
  { name: "Programs", icon: ProgramIcon },
  { name: "Codes", icon: LaptopIcon },
  { name: "Application", icon: IntelligenceIcon },
  { name: "Analytics", icon: AnalyticsIcon },
];

const { RangePicker } = DatePicker;

const planOptions = [
  { value: "all", label: "All plans" },
  { value: "starter", label: "Starter" },
  { value: "growth", label: "Growth" },
];

const tenantSummaryOptions = [
  { value: "all", label: "All Tenants" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
];

const tenants = [
  {
    company: "New Customer Promotion",
    plan: "Growth",
    status: "Active",
    user: "20",
    mrp: "$14,900",
    createdDate: "2025/12/09",
    Creator: "John Doe",
    Code: "WELCOME25",
  },
  {
    company: "New Customer Promotion",
    plan: "Growth",
    status: "Expired",
    user: "50",
    mrp: "$14,400",
    createdDate: "2025/12/09",
    Creator: "John Doe",
    Code: "WELCOME25",
  },
];

// Form Validation Schema
const validationSchema = Yup.object().shape({
  programName: Yup.string()
    .required("Program name is required")
    .min(2, "Program name must be at least 2 characters"),
  discountValue: Yup.number()
    .required("Discount value is required")
    .positive("Discount value must be positive")
    .integer("Discount value must be a whole number"),
  discountType: Yup.string().required("Discount type is required"),
  duration: Yup.string()
    .required("Duration is required")
    .oneOf(["billing", "forever", "custom"], "Invalid duration"),
  maxRedemption: Yup.number()
    .required("Max redemption is required")
    .positive("Max redemption must be positive")
    .integer("Max redemption must be a whole number"),
  approvalThreshold: Yup.number()
    .required("Approval threshold is required")
    .positive("Approval threshold must be positive")
    .integer("Approval threshold must be a whole number")
    .max(100, "Approval threshold cannot exceed 100"),
});

// Custom Select Styles
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    padding: "4px 8px",
    minHeight: "42px",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#3b82f6",
    },
    borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#3b82f6"
      : state.isFocused
      ? "#eff6ff"
      : "white",
    color: state.isSelected ? "white" : "#374151",
    "&:hover": {
      backgroundColor: state.isSelected ? "#3b82f6" : "#eff6ff",
    },
    '&:active': {
      backgroundColor: state.isSelected ? "#3b82f6" : "#f0f4ff",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#374151",
  }),
};

const DiscountManagement = () => {
  const dispatch = useDispatch();
  const [openRow, setOpenRow] = useState(null);
  const [activeTab, setActiveTab] = useState(tabs[0].name);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(planOptions[0]);
  const [selectedSummary, setSelectedSummary] = useState(
    tenantSummaryOptions[0]
  );
  const [selectedDate, setSelectedDate] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [showModal, setShowModal] = useState(false);
  const Discount = useSelector((state) => state?.discountCoupons);
  const {
    discount_program_types,
    discount_eligibility_rules,
    createProgramLoader,
    programs,
  } = Discount || {};

  const dropdownRef = useRef(null);
  const formRef = useRef();
  const navigate = useNavigate();

  const discountTypeOptions = useMemo(() => {
    if (!discount_program_types || !Array.isArray(discount_program_types)) {
      return [
        { value: "percentage", label: "Percentage off" },
        { value: "fixed", label: "Fixed amount off" },
      ];
    }
    return discount_program_types.map((type) => ({
      value: type.id.toString(),
      label: type.name,
    }));
  }, [discount_program_types]);

  // Convert discount_eligibility_rules to eligibility options
  const eligibilityOptions = useMemo(() => {
    if (
      !discount_eligibility_rules ||
      !Array.isArray(discount_eligibility_rules)
    ) {
      return [
        { id: "newCustomers", label: "New customers" },
        { id: "existingCustomers", label: "Existing Customers" },
        { id: "enterprisePlans", label: "Enterprise Plans" },
        { id: "partnerReferrals", label: "Partner Referrals" },
        { id: "trialUsers", label: "Trial Users" },
        { id: "atRiskCustomers", label: "At-risk customers" },
      ];
    }
    return discount_eligibility_rules.map((rule) => ({
      id: rule.id.toString(),
      label: rule.name,
    }));
  }, [discount_eligibility_rules]);

  // Create initial eligibility rules object based on dynamic data
  const initialEligibilityRules = useMemo(() => {
    const rules = {};
    if (
      discount_eligibility_rules &&
      Array.isArray(discount_eligibility_rules)
    ) {
      discount_eligibility_rules.forEach((rule) => {
        rules[rule.id.toString()] = false;
      });
    } else {
      // Fallback to default rules if no data
      rules.newCustomers = false;
      rules.existingCustomers = false;
      rules.enterprisePlans = false;
      rules.partnerReferrals = false;
      rules.trialUsers = false;
      rules.atRiskCustomers = false;
    }
    return rules;
  }, [discount_eligibility_rules]);

  // Initial Form Values
  const initialValues = {
    programName: "",
    discountValue: "",
    discountType:
      discountTypeOptions.length > 0 ? discountTypeOptions[0].value : "",
    duration: "billing",
    eligibilityRules: initialEligibilityRules,
    maxRedemption: "",
    approvalThreshold: "",
  };

  const durationOptions = [
    { value: "billing", label: "Billing cycle" },
    { value: "forever", label: "Forever" },
    { value: "custom", label: "Custom period" },
  ];

  // Close dropdown when clicking outside

  // useEffect(() => {
  //   dispatch(
  //     reqToGetDiscountVariables({
  //       data: { action: "program" },
  //       onSuccess: () => {},
  //       onFailure: () => {},
  //     })
  //   );
  //   dispatch(
  //     reqToGetProgramList({
  //       data: {},
  //       onSuccess: () => {},
  //       onFailure: () => {},
  //     })
  //   );
  // }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    dispatch(
      reqToGetProgramList({
        data: { page: currentPage, limit: pageSize },
        onSuccess: () => setLoading(false),
        onFailure: () => setLoading(false),
      })
    );
  }, [dispatch, currentPage, pageSize]);

  useEffect(() => {
    dispatch(
      reqToGetDiscountVariables({
        data: { action: "program" },
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenRow(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter tenants
  const filteredTenants = useMemo(() => {
    let result = tenants;

    // Plan filter
    if (selectedPlan.value !== "all") {
      result = result.filter(
        (t) => t.plan.toLowerCase() === selectedPlan.value
      );
    }

    // Summary filter
    if (selectedSummary.value === "active") {
      result = result.filter((t) => t.status === "Active");
    } else if (selectedSummary.value === "trial") {
      result = result.filter((t) => t.status === "Trial");
    }

    // Date filter
    if (selectedDate.length === 2) {
      const [start, end] = selectedDate;
      result = result.filter((t) => {
        const lastActiveDate = dayjs(t.lastActive);
        return (
          lastActiveDate.isAfter(start, "day") &&
          lastActiveDate.isBefore(end, "day")
        );
      });
    }

    return result;
  }, [selectedPlan, selectedSummary, selectedDate]);

  const totalCount = filteredTenants.length;
  const totalPages = Math.ceil(totalCount / pageSize);

  const paginatedTenants = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTenants.slice(start, start + pageSize);
  }, [filteredTenants, currentPage, pageSize]);

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  const goToPage = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleEditProgram = (programId) => {
    const programToEdit = programs?.result?.find(
      (program) => program.id === programId
    );
    if (programToEdit) {
      setModalMode("edit");
      setEditingProgram(programToEdit);
      setShowModal(true);
    }
  };

  const handleFormSubmit = async (values, { resetForm }) => {
    // Prepare eligibility rules array from checked values
    const selectedEligibilityRules = Object.keys(values.eligibilityRules)
      .filter((key) => values.eligibilityRules[key])
      .map((key) => parseInt(key)); // Convert to numbers as per API

    // Prepare payload according to your curl structure
    const payload = {
      name: values.programName,
      discount_type: parseInt(values.discountType), // Convert to number
      discount_value: parseInt(values.discountValue) || 0,
      duration:
        values.duration === "billing"
          ? 1
          : values.duration === "forever"
          ? 999
          : 30, // Adjust based on your duration mapping
      eligibility_rules: selectedEligibilityRules,
      max_redemptions: parseInt(values.maxRedemption) || 0,
      approval_threshold: parseInt(values.approvalThreshold) || 0,
      // status: "active",
    };

    dispatch(
      reqToCreateDiscountProgram({
        data: payload,
        onSuccess: (response) => {
          resetForm();
          setShowModal(false);
          dispatch(
            reqToGetProgramList({
              data: { page: currentPage, limit: pageSize },
              onSuccess: () => setLoading(false),
              onFailure: () => setLoading(false),
            })
          );
        },
        onFailure: (error) => {
          console.error("Error creating program:", error);
        },
      })
    );
  };

  // Function to handle modal confirm button click
  const handleModalConfirm = () => {
    if (formRef.current) {
      formRef.current.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true })
      );
    }
  };

  return (
    <>
      <div className="rounded-md border-[1px] border-solid border-[#F3EFEF] space-y-8 pb-6">
        {/* Header */}
        <div className="p-3 sm:p-6 bg-[#F5FFBD] space-y-6">
          <div className="mb-4">
            <button
              type="button"
              className="text-[16px] font-normal flex gap-1"
              onClick={() => navigate(-1)}
            >
              {BackArrowIcon} Back
            </button>
          </div>
          <div className="heading flex gap-2 justify-between items-end">
            <div className="heading-title space-y-1">
              <h1 className="text-h2 !font-medium mb-0 text-[#4285F4]">
                Discount Management
              </h1>
              <p className="text-span text-dark mb-0">
                Control revenue levers, prevent abuse, and track performance
              </p>
            </div>
          </div>
        </div>

        <div className="sm:p-6 p-3 !pt-0">
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between bg-[#EDF7FF] rounded-xl sm:p-6 p-3 mb-6">
            {/* ICON & DETAILS */}
            <div className="grid md:flex items-start gap-2">
              <span className="min-w-[40px]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                >
                  <rect width="40" height="40" rx="20" fill="#4285F4"></rect>
                  <path
                    d="M20.0003 28.3337C24.6027 28.3337 28.3337 24.6027 28.3337 20.0003C28.3337 15.398 24.6027 11.667 20.0003 11.667C15.398 11.667 11.667 15.398 11.667 20.0003C11.667 24.6027 15.398 28.3337 20.0003 28.3337Z"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                  <path
                    d="M20 23.333V19.583"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                  <path
                    d="M20 16.6763V16.668"
                    stroke="white"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  Governance-Centric Discount Control
                </span>
                <p className="text-gray-500 text-sm mb-0">
                  Manage promotional programs, unique codes, tenant
                  applications, and analytics with built-in fraud prevention and
                  audit trails. Integrates with Stripe/Paddle for seamless
                  billing operations.
                </p>
              </div>
              <div className="flex gap-4">
                <button className="btn-light-blue">
                  {WhiteBellIcon} Security
                </button>
                <button className="btn-light-gray  !text-[#4285F4]">
                  {BlueDownloadExportIcon} Alerts
                </button>
              </div>
            </div>
          </div>
          {/* Filters */}
          <div className="px-3 sm:px-6">
            <div className="flex !justify-between items-center mb-6 gap-4 flex-wrap w-full">
              <TabsHeader
                tabs={tabs}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                className="!mb-0 !w-auto "
              />
              {activeTab === "Programs" && (
                <button
                  className="btn-light-blue"
                  onClick={() => setShowModal(true)}
                >
                  {addNewTrentIcon} Add New Program
                </button>
              )}
            </div>
            {activeTab === "Programs" && (
              <ProgramsTab
                tenants={programs || []}
                totalCount={programs?.totalCount || 0}
                openRow={openRow}
                setOpenRow={setOpenRow}
                dropdownRef={dropdownRef}
                currentPage={currentPage}
                pageSize={pageSize}
                totalPages={Math.ceil((programs?.totalCount || 0) / pageSize)}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
                loading={loading}
                onEditProgram={handleEditProgram}
              />
            )}
            {activeTab === "Codes" && (
              <CodesTab
                openRow={openRow}
                setOpenRow={setOpenRow}
                dropdownRef={dropdownRef}
              />
              // <CodesTab
              //   tenants={filteredTenants}
              //   totalCount={totalCount}
              //   openRow={openRow}
              //   setOpenRow={setOpenRow}
              //   dropdownRef={dropdownRef}
              //   currentPage={currentPage}
              //   pageSize={pageSize}
              //   totalPages={totalPages}
              //   setCurrentPage={setCurrentPage}
              //   setPageSize={setPageSize}
              // />
            )}
            {activeTab === "Application" && (
              <ApplicationTab
                tenants={filteredTenants}
                totalCount={totalCount}
                openRow={openRow}
                setOpenRow={setOpenRow}
                dropdownRef={dropdownRef}
                currentPage={currentPage}
                pageSize={pageSize}
                totalPages={totalPages}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
              />
            )}
            {activeTab === "Analytics" && (
              <AnalyticsTabs
                totalCount={totalCount}
                openRow={openRow}
                setOpenRow={setOpenRow}
                dropdownRef={dropdownRef}
                currentPage={currentPage}
                pageSize={pageSize}
                totalPages={totalPages}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
              />
            )}
          </div>
        </div>
      </div>
      {/* Dynamic PopUpModal Integration */}
      <PopUpModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleModalConfirm}
        title="Create New Program"
        confirmText={createProgramLoader ? "Creating..." : "Create Program"}
        cancelText="Close"
        confirmDisabled={createProgramLoader}
        loading={createProgramLoader}
        size="large"
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, errors, touched, handleSubmit }) => (
            <Form ref={formRef}>
              {/* Dynamic Form Content */}
              <div className="space-y-4">
                {/* Program Name */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Program Name *
                  </label>
                  <Field
                    type="text"
                    name="programName"
                    className="input-field w-full border p-2 rounded-md"
                    placeholder="Program name"
                  />
                  <ErrorMessage
                    name="programName"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>

                {/* Discount Value and Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Discount Value *
                    </label>
                    <Field
                      type="number"
                      name="discountValue"
                      className="input-field w-full border p-2 rounded-md"
                      placeholder="25"
                    />
                    <ErrorMessage
                      name="discountValue"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Discount Type *
                    </label>
                    <Select
                      options={discountTypeOptions}
                      value={discountTypeOptions.find(
                        (option) => option.value === values.discountType
                      )}
                      onChange={(selectedOption) =>
                        setFieldValue("discountType", selectedOption.value)
                      }
                      styles={customSelectStyles}
                      isSearchable={false}
                    />
                    <ErrorMessage
                      name="discountType"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Duration *
                  </label>
                  <Select
                    options={durationOptions}
                    value={durationOptions.find(
                      (option) => option.value === values.duration
                    )}
                    onChange={(selectedOption) =>
                      setFieldValue("duration", selectedOption.value)
                    }
                    styles={customSelectStyles}
                    isSearchable={false}
                  />
                  <ErrorMessage
                    name="duration"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>

                {/* Eligibility Rules */}
                <div className="form-group">
                  <label className="block text-sm font-medium mb-3">
                    Eligibility Rules *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {eligibilityOptions.map((option) => (
                      <label
                        key={option.id}
                        className="flex items-center gap-3 cursor-pointer select-none"
                      >
                        <div className="relative !h-5 !w-5">
                          <Field
                            type="checkbox"
                            name={`eligibilityRules.${option.id}`}
                            className="peer hidden !p-0"
                          />
                          <span className="flex h-5 w-5 items-center justify-center rounded-[6px] border-2 border-[#9BA2AB] peer-checked:border-[#4285F4] bg-white peer-checked:bg-white"></span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="hidden h-4 w-4 text-[#4285F4] peer-checked:block absolute top-[90%] left-[90%] -translate-x-1/2 -translate-y-1/2"
                            fill="#4285F4"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="text-black text-span !leading-[15px] !tracking-[0]">
                          {option.label}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Max Redemption and Approval Threshold */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Max Redemption *
                    </label>
                    <Field
                      type="number"
                      name="maxRedemption"
                      className="input-field w-full border p-2 rounded-md"
                      placeholder="1000"
                    />
                    <ErrorMessage
                      name="maxRedemption"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Approval Threshold *
                    </label>
                    <Field
                      type="number"
                      name="approvalThreshold"
                      className="input-field w-full border p-2 rounded-md"
                      placeholder="20"
                    />
                    <ErrorMessage
                      name="approvalThreshold"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Hidden submit button for Formik */}
              <button type="submit" className="hidden" />
            </Form>
          )}
        </Formik>
      </PopUpModal>
    </>
  );
};

export default DiscountManagement;
